<template>
  <!-- On a phone: the prototype's Polls sheet (sheetPolls). -->
  <div v-if="phonePolls" class="polls-phone" data-testid="polls-phone">
    <!-- The host's new poll: a question and two to four options. -->
    <template v-if="isAdminOrSpeaker && phoneDraft.open">
      <div class="pp-body">
        <h3 class="pp-h">New poll</h3>
        <p v-if="phoneActive.length" class="pp-note pp-note--left">A poll is live now. Launching this one closes it.</p>
        <div class="pp-fld">
          <label for="pp-question">Question</label>
          <input id="pp-question" v-model="phoneDraft.question" placeholder="What do you want to ask?" data-testid="polls-new-question">
        </div>
        <div v-for="(_, i) in phoneDraft.options" :key="i" class="pp-fld">
          <label :for="`pp-option-${i}`">Option {{ i + 1 }}</label>
          <input :id="`pp-option-${i}`" v-model="phoneDraft.options[i]" :placeholder="`Option ${i + 1}`" :data-testid="`polls-new-option-${i}`">
        </div>
        <button v-if="phoneDraft.options.length < 4" type="button" class="pp-btn pp-btn--g pp-btn--sm" data-testid="polls-new-add" @click="addPhoneOption">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
          Add an option
        </button>
        <p v-if="phoneError" class="pp-error" role="alert" data-testid="polls-new-error">{{ phoneError }}</p>
      </div>
      <div class="pp-foot pp-foot--two">
        <button type="button" class="pp-btn pp-btn--g" data-testid="polls-new-cancel" @click="cancelPhoneDraft">Cancel</button>
        <button type="button" class="pp-btn" data-testid="polls-new-launch" @click="launchFromPhone">Launch poll</button>
      </div>
    </template>

    <template v-else>
      <div class="pp-body">
        <div v-if="!loaded" class="widget-loader"><div class="shimmer card-shimmer"></div></div>

        <div v-else-if="!phoneHasPolls" class="pp-empty" data-testid="polls-empty">
          <span class="pp-empty__g" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 20V12M12 20V5M19 20v-6M3 20h18"/></svg>
          </span>
          <b>No polls yet</b>
          <p>{{ isAdminOrSpeaker
            ? 'Ask the room a quick question. Results appear for everyone in seconds.'
            : 'When the host asks the room something, it appears here.' }}</p>
          <button v-if="isAdminOrSpeaker" type="button" class="pp-btn pp-btn--create" data-testid="polls-create" @click="startPhoneDraft">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
            Create a poll
          </button>
        </div>

        <template v-else>
          <div class="pp-seg" role="group" aria-label="Polls">
            <button type="button" :class="{ on: phoneTab === 'active' }" :aria-pressed="String(phoneTab === 'active')" data-testid="polls-tab-active" @click="phoneTab = 'active'">Active<i>{{ phoneActive.length }}</i></button>
            <button type="button" :class="{ on: phoneTab === 'closed' }" :aria-pressed="String(phoneTab === 'closed')" data-testid="polls-tab-closed" @click="phoneTab = 'closed'">Closed<i>{{ phoneClosed.length }}</i></button>
          </div>

          <p v-if="!phoneList.length" class="pp-note" data-testid="polls-tab-empty">
            {{ phoneTab === 'active' ? 'No poll is live right now.' : 'Nothing closed yet.' }}
          </p>

          <div v-for="poll in phoneList" :key="poll._id" class="pp-card" :data-testid="`polls-card-${poll._id}`">
            <!-- Not voted yet: pick, then submit; the results come after. -->
            <template v-if="asksForVote(poll)">
              <span class="pp-st pp-st--live">Live poll</span>
              <p class="pp-q">{{ poll.question }}</p>
              <button
                  v-for="(opt, i) in poll.options"
                  :key="i"
                  type="button"
                  class="pp-opt"
                  :class="{ on: pickedFor(poll).includes(i), square: poll.type === 'multiple' }"
                  :role="poll.type === 'multiple' ? 'checkbox' : 'radio'"
                  :aria-checked="String(pickedFor(poll).includes(i))"
                  :data-testid="`polls-pick-${i}`"
                  @click="togglePick(poll, i)"
              ><i aria-hidden="true"></i>{{ opt.text }}</button>
              <button type="button" class="pp-btn pp-btn--blk" :disabled="!pickedFor(poll).length" data-testid="polls-submit" @click="submitPhoneVote(poll)">Submit vote</button>
              <p class="pp-meta pp-meta--c">{{ showResults(poll) ? 'Results show once you vote.' : 'Your vote goes to the host.' }}</p>
            </template>

            <!-- Voted, closed, or the host's own: how it stands. -->
            <template v-else>
              <span class="pp-st" :class="isLivePoll(poll) ? 'pp-st--live' : 'pp-st--no'" data-testid="polls-status">
                {{ isLivePoll(poll) ? (isAdminOrSpeaker ? 'Live · results update as people vote' : 'Live · vote submitted') : 'Closed' }}
              </span>
              <p class="pp-q">{{ poll.question }}</p>
              <template v-if="showResults(poll)">
                <div
                    v-for="(opt, i) in poll.options"
                    :key="i"
                    class="pp-res"
                    :class="{ top: i === leadingIndex(poll), 'top--label-in': i === leadingIndex(poll) && getVotePercent(poll, i) >= 45, 'top--pct-in': i === leadingIndex(poll) && getVotePercent(poll, i) >= 90 }"
                    :data-testid="`polls-result-${i}`"
                >
                  <i :style="{ width: `${getVotePercent(poll, i)}%` }"></i>
                  <span>
                    {{ opt.text }}
                    <svg v-if="hasVoted(poll, i)" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-label="Your choice" data-testid="polls-mine"><path d="m5.8 12.3 4.2 4.2L18.2 7.8"/></svg>
                  </span>
                  <em>{{ getVotePercent(poll, i) }}%</em>
                </div>
                <p class="pp-meta" data-testid="polls-meta">{{ phoneMeta(poll) }}</p>
              </template>
              <p v-else class="pp-meta" data-testid="polls-meta">
                <template v-if="votedIn(poll)">You chose {{ chosenLine(poll) }}. </template>The host hasn't shared the results.
              </p>
              <button v-if="isAdminOrSpeaker && isLivePoll(poll)" type="button" class="pp-btn pp-btn--g pp-btn--blk pp-btn--close" data-testid="polls-close" @click="closePoll(poll._id)">Close poll</button>
            </template>
          </div>
        </template>
      </div>

      <div v-if="isAdminOrSpeaker && phoneHasPolls" class="pp-foot">
        <button type="button" class="pp-btn pp-btn--blk" data-testid="polls-new" @click="startPhoneDraft">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
          New poll
        </button>
      </div>
    </template>
  </div>

  <div v-else class="polls-widget">
    <!-- Create/Edit View -->
    <div v-if="viewMode === 'create' || viewMode === 'edit'" class="create-view">
      <div class="create-header">
        <button type="button" @click="cancelCreate" class="back-btn">
          <IconCaretLeft :size="16" aria-hidden="true"></IconCaretLeft> {{ viewMode === 'edit' ? 'Edit Poll' : 'New Poll' }}
        </button>
      </div>

      <div class="create-form">
        <div v-if="errorMessage" class="error-banner" role="alert">
          {{ errorMessage }}
        </div>
        <input
            v-model="pollForm.question"
            placeholder="Poll question"
            aria-label="Poll question"
            class="poll-input question-input"
        />

        <div
            v-for="(opt, i) in pollForm.options"
            :key="i"
            class="option-row"
        >
          <span class="radio-circle" :class="{ square: pollForm.type === 'multiple' }" aria-hidden="true"></span>
          <input
              v-model="pollForm.options[i]"
              :placeholder="`Option ${i + 1}`"
              :aria-label="`Option ${i + 1}`"
              class="poll-input option-input"
          />
          <button
              v-if="pollForm.options.length > 2"
              type="button"
              class="icon-btn remove-opt-btn"
              title="Remove"
              :aria-label="`Remove option ${i + 1}`"
              @click="removeOption(i)"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m7.1 7.1 9.8 9.8M16.9 7.1l-9.8 9.8"/></svg>
          </button>
        </div>

        <button
            type="button"
            @click="addOption"
            :disabled="pollForm.options.length >= 10"
            class="add-opt-btn"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 6.2v11.6M6.2 12h11.6"/></svg>
          Add Option
        </button>

        <div class="poll-setting">
          <XlSwitch
              v-model="pollForm.allowViewResults"
              label="Results visible to all"
          />
        </div>

        <div class="poll-setting">
          <XlSwitch
              :model-value="pollForm.type === 'multiple'"
              label="Has multiple option"
              @update:model-value="pollForm.type = $event ? 'multiple' : 'single'"
          />
        </div>

        <div class="form-actions">
          <XlTooltip text="Delete" control>
            <button type="button" class="icon-btn delete-btn" aria-label="Delete" @click="deleteDraft">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m7.1 7.1 9.8 9.8M16.9 7.1l-9.8 9.8"/></svg>
            </button>
          </XlTooltip>
          <div class="right-actions">
            <XlButton variant="ghost" size="sm" @click="openNow">Open Now</XlButton>
            <XlButton size="sm" @click="saveDraft">Save</XlButton>
          </div>
        </div>
      </div>
    </div>

    <!-- List View -->
    <div v-else class="list-view">
      <div v-if="!loaded" class="widget-loader" aria-hidden="true">
        <div class="shimmer tabs-shimmer"></div>
        <div class="shimmer card-shimmer"></div>
        <div class="shimmer card-shimmer"></div>
      </div>
      <template v-else>
        <!-- Tabs -->
        <div class="polls-tabs">
          <div class="seg" role="tablist" aria-label="Polls">
            <button
                v-for="tab in tabsData"
                :key="tab.id"
                type="button"
                role="tab"
                :aria-selected="activeTab === tab.id"
                :class="{ on: activeTab === tab.id }"
                @click="activeTab = tab.id"
            >{{ tab.label }}</button>
          </div>
        </div>

        <div class="polls-tabs-content">
        <div class="polls-list-content">
          <!-- Open Tab -->
          <div v-if="activeTab === 'Open'" :class="{ 'has-records': !!openPoll }">
            <div v-if="openPoll" class="poll-card active">
              <div class="card-header">
                <h3 class="poll-question">{{ openPoll.question }}</h3>
                <button
                    v-if="isAdminOrSpeaker"
                    type="button"
                    @click="closePoll(openPoll._id)"
                    class="close-icon-btn"
                    title="End Poll"
                    aria-label="End Poll"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m7.1 7.1 9.8 9.8M16.9 7.1l-9.8 9.8"/></svg>
                </button>
              </div>

              <div class="poll-options" :class="{ 'with-results': showResults(openPoll) }">
                <div v-for="(opt, i) in openPoll.options" :key="i" class="poll-option">
                  <button
                      type="button"
                      @click="vote(openPoll._id, i)"
                      :class="['option-btn', { selected: hasVoted(openPoll, i) }]"
                      :disabled="!isAdminOrSpeaker && openPoll.status !== 'published'"
                      :aria-pressed="hasVoted(openPoll, i)"
                  >
                  <span class="check-circle" :class="{ square: openPoll.type === 'multiple', voted: hasVoted(openPoll, i) }" aria-hidden="true">
                    <span v-if="hasVoted(openPoll, i)" class="dot"></span>
                  </span>
                    <span class="opt-text">{{ opt.text }}</span>
                    <span v-if="showResults(openPoll)" class="vote-count">{{ opt.voteCount }} votes</span>
                  </button>
                  <div v-if="showResults(openPoll)" class="progress-track" aria-hidden="true">
                    <div class="progress-bar" :style="{ width: getVotePercent(openPoll, i) + '%' }"></div>
                  </div>
                </div>
              </div>

              <div v-if="isAdminOrSpeaker" class="admin-controls">
                <div class="results-toggle">
                  <XlSwitch
                      size="sm"
                      :model-value="openPoll.allowViewResults"
                      label="Results visible to all"
                      @change="toggleResults(openPoll)"
                  />
                </div>
                <XlButton variant="ghost" size="sm" @click="closePoll(openPoll._id)">End Poll</XlButton>
              </div>
            </div>
            <div v-else class="empty-state">
              <span class="empty-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M4.2 19.8h15.6"/><path d="M7.6 19.8v-6M12 19.8V7.2M16.4 19.8v-9"/></svg>
              </span>
              <p v-if="isAdminOrSpeaker" class="empty-text">To create a poll, click the<br>“New Poll” button below</p>
              <p v-else class="empty-text">No active poll</p>
            </div>
          </div>

          <!-- Closed Tab -->
          <div v-if="activeTab === 'Closed'" :class="{ 'has-records': closedPolls.length > 0 }">
            <div v-for="poll in closedPolls" :key="poll._id" class="poll-card closed">
              <div class="card-header">
                <h3 class="poll-question">{{ poll.question }}</h3>
                <span class="status-label">Closed</span>
              </div>
              <!-- Simplified results for closed polls -->
              <div class="poll-options results">
                <div v-for="(opt, i) in poll.options" :key="i" class="poll-option static">
                  <div class="option-row-static">
                    <span class="opt-text" :title="opt.text">{{ opt.text }}</span>
                    <span v-if="showResults(poll)" class="vote-count">{{ opt.voteCount }} votes</span>
                  </div>
                  <div v-if="showResults(poll)" class="progress-track" aria-hidden="true">
                    <div class="progress-bar" :style="{ width: getVotePercent(poll, i) + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="closedPolls.length === 0" class="empty-state compact">
              <p>No closed polls</p>
            </div>
          </div>

          <div v-if="activeTab === 'Saved'" :class="{ 'has-records': savedPolls.length > 0 }">
            <div v-for="poll in savedPolls" :key="poll._id" class="poll-card draft">
              <div class="draft-header-content">
                <h3 class="poll-question">{{ poll.question }}</h3>
                <button type="button" class="icon-btn expand-btn" aria-label="Expand">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m6 9.8 6 6 6-6"/></svg>
                </button>
              </div>
              <div class="status-saved">Saved</div>

              <div class="draft-preview">
                <div v-for="(opt, i) in poll.options" :key="i" class="preview-opt">
                  <span class="circle-icon" :class="{ square: poll.type === 'multiple' }" aria-hidden="true"></span>
                  <span class="opt-text">{{ opt.text }}</span>
                </div>
              </div>

              <div class="draft-settings">
                <div class="setting-row">
                  <XlSwitch
                      size="sm"
                      :model-value="poll.allowViewResults"
                      label="Results visible to all"
                      @change="toggleResults(poll)"
                  />
                </div>
              </div>

              <div class="draft-actions" v-if="isAdminOrSpeaker">
                <XlTooltip text="Delete" control>
                  <button type="button" class="icon-btn delete-btn" aria-label="Delete" @click="deletePoll(poll._id)">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m7.1 7.1 9.8 9.8M16.9 7.1l-9.8 9.8"/></svg>
                  </button>
                </XlTooltip>
                <button type="button" class="lnk" @click="editPoll(poll)">Edit</button>
                <XlButton size="sm" class="push-right" @click="openSavedPoll(poll._id)">Open Poll</XlButton>
              </div>
            </div>
            <div v-if="savedPolls.length === 0" class="empty-state compact">
              <p>No saved polls</p>
            </div>
          </div>
        </div>
        </div>


        <!-- Footer -->
        <div class="polls-footer">
           <div v-if="isAdminOrSpeaker && viewMode === 'list'" class="footer-actions">
              <button type="button" class="pnew" @click="startCreate">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 6.2v11.6M6.2 12h11.6"/></svg>
                New Poll
              </button>
           </div>
        </div>
      </template>
    </div>
    <SessionDialog v-if="modalRemovePoll" title="Delete Poll" tone="cr" icon="trash" dismissible data-testid="poll-delete-dialog" @close="modalRemovePoll = null">
      <p>Are you sure you want to delete this poll?</p>
      <template #actions>
        <XlButton variant="ghost" @click="modalRemovePoll = null">Cancel</XlButton>
        <XlButton variant="danger" @click="confirmDeletePoll">Delete</XlButton>
      </template>
    </SessionDialog>
    <SessionDialog v-if="modalConfirmLaunch" title="Launch poll?" tone="wa" icon="info" dismissible data-testid="poll-launch-dialog" @close="modalConfirmLaunch = false">
      <p>A poll is currently live. Launching this new poll will close the current one.</p>
      <template #actions>
        <XlButton variant="ghost" @click="modalConfirmLaunch = false">Cancel</XlButton>
        <XlButton @click="confirmLaunch">Close &amp; Launch New</XlButton>
      </template>
    </SessionDialog>
  </div>
</template>

<script setup>
import {ref, computed, onMounted, onUnmounted, reactive, watch} from "vue";
import {useSocketStore} from "../../stores/socketStore";
import {useAuthStore} from "../../stores/auth";
import {useSessionStore} from "../../stores/sessionStore";
import SessionDialog from "../../livekit-client/components/SessionDialog.vue";
import XlButton from "../xl/XlButton.vue";
import XlSwitch from "../xl/XlSwitch.vue";
import XlTooltip from "../xl/XlTooltip.vue";
import IconCaretLeft from "../icons/IconCaretLeft.vue";

const props = defineProps({
  sessionId: String,
  userId: String,
  userRole: String,
  userName: String,
  domain: String,
  isActive: {
    type: Boolean,
    default: false
  }
});

const socketStore = useSocketStore();
const polls = ref([]);
const activeTab = ref("Open");
const loaded = ref(false);
const viewMode = ref("list"); // 'list', 'create', 'edit'
const editingId = ref(null);
const errorMessage = ref("");
const unreadCount = ref(0);

const pollForm = reactive({
  question: "",
  options: ["", ""],
  allowViewResults: false,
  type: 'single', // 'single' | 'multiple'
});
const modalRemovePoll = ref(null);

const isAdminOrSpeaker = computed(() => {
  const role = props.userRole?.toLowerCase();
  const allowedRoles = ['administrator', 'speaker', 'admin', 'event-planner', 'owner'];

  // Check prop role
  if (allowedRoles.includes(role)) return true;

  // Fallback to authStore role
  const storeRole = useAuthStore().userRole?.toLowerCase();
  if (allowedRoles.includes(storeRole)) return true;

  return false;
});

// Helper for visibility
const showResults = (poll) => isAdminOrSpeaker.value || (poll && poll.allowViewResults);

// Computed Lists
const openPoll = computed(() => polls.value.find(p => p.status === 'published' || p.status === 'open'));
const closedPolls = computed(() => polls.value.filter(p => p.status === 'closed'));
const savedPolls = computed(() => polls.value.filter(p => p.status === 'draft' || p.status === 'saved'));

const visibleTabs = computed(() => {
  const tabs = ['Open', 'Closed'];
  if (isAdminOrSpeaker.value) {
    tabs.push('Saved');
  }
  return tabs;
});

const tabsData = computed(() => {
  return visibleTabs.value.map(t => ({ id: t, label: t }));
});

// Actions
const startCreate = () => {
  resetForm();
  viewMode.value = 'create';
};

const cancelCreate = () => {
  viewMode.value = 'list';
  resetForm();
};

const resetForm = () => {
  pollForm.question = "";
  pollForm.options = ["", ""];
  pollForm.allowViewResults = false;
  pollForm.type = 'single';
  editingId.value = null;
};

const addOption = () => {
  if (pollForm.options.length < 10) {
    pollForm.options.push("");
  }
};

const removeOption = (index) => {
  if (pollForm.options.length > 2) {
    pollForm.options.splice(index, 1);
  }
};

const saveDraft = () => {
  if (!validateForm()) return;

  if (viewMode.value === 'edit' && editingId.value) {
    socketStore.emit("poll:update", {
      pollId: editingId.value,
      question: pollForm.question,
      options: pollForm.options.filter(o => o.trim()),
      allowViewResults: pollForm.allowViewResults,
      type: pollForm.type
    });
  } else {
    socketStore.emit("poll:create", {
      sessionId: props.sessionId,
      domain: props.domain || window.store.subdomain,
      userId: props.userId,
      question: pollForm.question,
      options: pollForm.options.filter(o => o.trim()),
      status: 'draft',
      allowViewResults: pollForm.allowViewResults,
      type: pollForm.type
    });
  }
  viewMode.value = 'list';
  activeTab.value = 'Saved';
};

const modalConfirmLaunch = ref(false);
const pendingLaunch = ref(null);

const openNow = () => {
  if (!validateForm()) return;

  if (openPoll.value) {
    pendingLaunch.value = () => executeOpenNow();
    modalConfirmLaunch.value = true;
    return;
  }

  executeOpenNow();
};

const executeOpenNow = () => {
  if (viewMode.value === 'edit' && editingId.value) {
    socketStore.emit("poll:update", {
      pollId: editingId.value,
      question: pollForm.question,
      options: pollForm.options.filter(o => o.trim()),
      allowViewResults: pollForm.allowViewResults,
      type: pollForm.type
    });
    // After update, also update status to published
    socketStore.emit("poll:status_update", {
      pollId: editingId.value,
      status: 'published'
    });
  } else {
    socketStore.emit("poll:create", {
      sessionId: props.sessionId,
      domain: props.domain || window.store.subdomain,
      userId: props.userId,
      question: pollForm.question,
      options: pollForm.options.filter(o => o.trim()),
      status: 'published',
      allowViewResults: pollForm.allowViewResults,
      type: pollForm.type
    });
  }
  viewMode.value = 'list';
  activeTab.value = 'Open';
};

const validateForm = () => {
  const valid = pollForm.options.filter(o => o.trim());
  if (!pollForm.question.trim() || valid.length < 2) {
    errorMessage.value = "Please provide a question and at least 2 options.";
    setTimeout(() => errorMessage.value = "", 3000); // Auto-clear
    return false;
  }
  return true;
};

const deleteDraft = () => {
  if (editingId.value) {
    socketStore.emit("poll:delete", {pollId: editingId.value});
  }
  viewMode.value = 'list';
};

const editPoll = (poll) => {
  pollForm.question = poll.question;
  pollForm.options = poll.options.map(o => o.text);
  pollForm.allowViewResults = poll.allowViewResults;
  pollForm.type = poll.type || 'single';
  editingId.value = poll._id;
  viewMode.value = 'edit';
};

const openSavedPoll = (pollId) => {
  if (openPoll.value) {
    pendingLaunch.value = () => executeOpenSavedPoll(pollId);
    modalConfirmLaunch.value = true;
    return;
  }
  executeOpenSavedPoll(pollId);
};

const executeOpenSavedPoll = (pollId) => {
  socketStore.emit("poll:status_update", {pollId, status: 'published'});
  activeTab.value = 'Open';
};

const confirmLaunch = () => {
  if (pendingLaunch.value) {
    pendingLaunch.value();
    pendingLaunch.value = null;
  }
  modalConfirmLaunch.value = false;
};

const closePoll = (pollId) => {
  socketStore.emit("poll:status_update", {pollId, status: 'closed'});
};

const deletePoll = (pollId) => {
  modalRemovePoll.value = pollId;
};

const confirmDeletePoll = () => {
  if (modalRemovePoll.value) {
    socketStore.emit("poll:delete", {pollId: modalRemovePoll.value});
    modalRemovePoll.value = null;
  }
};

const vote = (pollId, optionIndex) => {
  socketStore.emit("poll:vote", {
    pollId,
    userId: props.userId,
    optionIndex
  });
};

/**
 * Whether this viewer is among the option's voters. The service records the
 * id from the verified chat token, a number; the page knows it as a string —
 * `includes` never matched, so a policyholder's vote counted on the host's
 * screen and showed nothing on their own (Polls → "their screen shows no sign
 * they voted").
 */
const hasVoted = (poll, optionIndex) => {
  if (!poll || !poll.options[optionIndex] || props.userId == null) return false;
  return (poll.options[optionIndex].voters ?? []).some((voter) => String(voter) === String(props.userId));
};

const getVotePercent = (poll, optionIndex) => {
  const total = poll.options.reduce((sum, opt) => sum + opt.voteCount, 0);
  if (total === 0) return 0;
  return Math.round((poll.options[optionIndex].voteCount / total) * 100);
};

// ----- Polls on a phone (Mobile in-session → Polls; prototype sheetPolls) -----
// The same polls and the same socket events, drawn as the prototype's sheet:
// Active and Closed; a participant picks and submits, then sees the bars; the
// host sees live results, closes a poll and launches a new one. The desktop
// pane is as it was.
const phoneStore = useSessionStore();
const phonePolls = computed(() => phoneStore.phoneRoom);
const phoneTab = ref('active');
const picks = reactive({});
const phoneDraft = reactive({ open: false, question: '', options: ['', ''] });
const phoneError = ref('');

const isLivePoll = (poll) => poll.status === 'published' || poll.status === 'open';
const phoneActive = computed(() => polls.value.filter(isLivePoll));
const phoneClosed = computed(() => polls.value.filter((poll) => poll.status === 'closed'));
const phoneList = computed(() => (phoneTab.value === 'active' ? phoneActive.value : phoneClosed.value));
const phoneHasPolls = computed(() => phoneActive.value.length + phoneClosed.value.length > 0);

const myChoices = (poll) => (poll.options ?? []).map((_, i) => (hasVoted(poll, i) ? i : -1)).filter((i) => i >= 0);
const votedIn = (poll) => myChoices(poll).length > 0;
// A participant who has not voted in a live poll is asked; anybody else sees how it stands.
const asksForVote = (poll) => isLivePoll(poll) && !isAdminOrSpeaker.value && !votedIn(poll);
const pickedFor = (poll) => picks[poll._id] ?? [];
const togglePick = (poll, index) => {
  if (poll.type === 'multiple') {
    const now = pickedFor(poll);
    picks[poll._id] = now.includes(index) ? now.filter((i) => i !== index) : [...now, index];
  } else {
    picks[poll._id] = [index];
  }
};
const submitPhoneVote = (poll) => {
  const chosen = pickedFor(poll);
  if (!chosen.length || !isLivePoll(poll)) return;
  chosen.forEach((index) => vote(poll._id, index));
  delete picks[poll._id];
};
const totalVotes = (poll) => (poll.options ?? []).reduce((sum, opt) => sum + (opt.voteCount || 0), 0);
const leadingIndex = (poll) => {
  const total = totalVotes(poll);
  if (!total) return -1;
  const top = Math.max(...poll.options.map((opt) => opt.voteCount || 0));
  return poll.options.findIndex((opt) => (opt.voteCount || 0) === top);
};
const chosenLine = (poll) => myChoices(poll).map((i) => `“${poll.options[i].text}”`).join(', ');
const phoneMeta = (poll) => {
  const n = totalVotes(poll);
  const votes = `${n} ${n === 1 ? 'vote' : 'votes'}`;
  return votedIn(poll) ? `${votes} · you chose ${chosenLine(poll)}` : votes;
};

const startPhoneDraft = () => {
  phoneDraft.open = true;
  phoneDraft.question = '';
  phoneDraft.options = ['', ''];
  phoneError.value = '';
};
const cancelPhoneDraft = () => {
  phoneDraft.open = false;
  phoneError.value = '';
};
const addPhoneOption = () => { if (phoneDraft.options.length < 4) phoneDraft.options.push(''); };
const launchFromPhone = () => {
  const question = phoneDraft.question.trim();
  const options = phoneDraft.options.map((o) => o.trim()).filter(Boolean);
  if (!question || options.length < 2) {
    phoneError.value = 'Add a question and at least two options.';
    return;
  }
  // Results for everybody, as the prototype's polls have them: "Results
  // appear for everyone in seconds."
  socketStore.emit("poll:create", {
    sessionId: props.sessionId,
    domain: props.domain || window.store.subdomain,
    userId: props.userId,
    question,
    options,
    status: 'published',
    allowViewResults: true,
    type: 'single',
  });
  phoneDraft.open = false;
  phoneError.value = '';
  phoneTab.value = 'active';
};

// A poll gone live: the phone's notice with Vote, for somebody who has not voted.
const tellOfLivePoll = (poll) => {
  if (!poll || !isLivePoll(poll) || isAdminOrSpeaker.value || votedIn(poll)) return;
  phoneStore.setPollNotice({ question: poll.question, at: Date.now() });
};

const handleAll = (all) => {
  if (all.length > 0 && all[0].sessionId !== props.sessionId) return;
  polls.value = all;
  loaded.value = true;
  // On initial load, if we have active polls and we are NOT active, these are "unread" to the user session
  if (!props.isActive) {
    unreadCount.value = polls.value.filter(p => p.status === 'published' || p.status === 'open').length;
  }
};

const handleCreated = (poll) => {
  if (poll.sessionId !== props.sessionId) return;
  if (!polls.value.find(p => p._id === poll._id)) {
    polls.value.unshift(poll);
    if (!props.isActive && (poll.status === 'published' || poll.status === 'open')) {
      unreadCount.value++;
    }
    tellOfLivePoll(poll);
  }
};

const handleUpdated = (updated) => {
  if (updated.sessionId !== props.sessionId) return;
  const idx = polls.value.findIndex(p => p._id === updated._id);
  // Check if status changed to published/open from something else, and we are not active
  if (idx !== -1) {
    const oldStatus = polls.value[idx].status;
    const newStatus = updated.status;
    if (!props.isActive && (newStatus === 'published' || newStatus === 'open') && (oldStatus !== 'published' && oldStatus !== 'open')) {
      unreadCount.value++;
    }
    if ((newStatus === 'published' || newStatus === 'open') && oldStatus !== 'published' && oldStatus !== 'open') {
      tellOfLivePoll(updated);
    }
    polls.value[idx] = updated;
  } else {
    // If not found, treat as new (rare edge case of update before create event?)
    polls.value.unshift(updated);
    if (!props.isActive && (updated.status === 'published' || updated.status === 'open')) {
      unreadCount.value++;
    }
  }
};

const handleDeleted = ({pollId}) => {
  polls.value = polls.value.filter(p => p._id !== pollId);
};

const toggleResults = (poll) => {
  if (!isAdminOrSpeaker.value) return;
  const newStatus = !poll.allowViewResults;

  // Only send the field we want to update
  socketStore.emit("poll:update", {
    pollId: poll._id,
    allowViewResults: newStatus
  });
};

onMounted(() => {
  socketStore.on("poll:all", handleAll);
  socketStore.on("poll:created", handleCreated);
  socketStore.on("poll:updated", handleUpdated);
  socketStore.on("poll:deleted", handleDeleted);

  fetchPollsData();
});


watch(() => props.isActive, (active) => {
  if (active) {
    unreadCount.value = 0;
  }
}, {immediate: true});

const emit = defineEmits(['update-notifications']);

watch(unreadCount, (newVal) => {
  emit('update-notifications', newVal);
}, {immediate: true});

onUnmounted(() => {
  socketStore.off("poll:all", handleAll);
  socketStore.off("poll:created", handleCreated);
  socketStore.off("poll:updated", handleUpdated);
  socketStore.off("poll:deleted", handleDeleted);
});

const fetchPollsData = () => {
  if (!props.sessionId) return;
  socketStore.emit("poll:get_all", {
    sessionId: props.sessionId,
    domain: props.domain || window.store.subdomain
  });
};

watch(() => props.sessionId, (newVal) => {
  if (newVal) {
    fetchPollsData();
  }
});

</script>

<style scoped lang="scss">
/* Polls pane, drawn to the session-panel prototype (cd149 "Polls, redrawn").
   Local aliases keep the token + prototype-hex fallback in one place. */
.polls-widget {
  --pw-pur: var(--xl-color-primary, #5E2F78);
  --pw-pur-2: var(--xl-color-surface-tint, #8154A6);
  --pw-tint: var(--xl-color-primary-container, #F5EFF9);
  --pw-line: var(--xl-color-border, #EDE7F0);
  --pw-line-2: var(--xl-color-border-subtle, #F6F2F8);
  --pw-ink: var(--xl-color-on-surface, #1C1720);
  --pw-ink-2: var(--xl-color-on-surface-variant, #514A57);
  --pw-mut: var(--xl-color-outline, #867D8D);
  --pw-faint: var(--xl-color-outline-variant, #B6AEBC);
  --pw-cr: var(--xl-color-error, #A32020);
  --pw-cr-bg: var(--xl-color-error-container, #FCEBEB);
  --pw-ok: var(--xl-color-success, #166B48);
  --pw-paper: var(--xl-color-surface, #FCFAFD);
  --pw-ring: #CFC8D6;

  flex: 1;
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  background: #fff;
  font-family: var(--xl-font-family, 'Lato', sans-serif);
  letter-spacing: -.006em;
  color: var(--pw-ink);

  *, *::before, *::after { box-sizing: border-box; }

  button:focus-visible,
  input:focus-visible {
    outline: 2px solid var(--pw-pur);
    outline-offset: 2px;
  }
}

/* ---------- shared bits ---------- */
/* Reset only this pane's own buttons: a bare `button` selector would also hit
   the root <button> of every XlButton, which carries this scope too. */
.back-btn, .icon-btn, .close-icon-btn, .lnk, .add-opt-btn, .option-btn, .pnew,
.polls-tabs .seg button {
  font: inherit;
  color: inherit;
  cursor: pointer;
  border: 0;
  background: none;
  padding: 0;
}

.icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border-radius: 9px;
  border: 1px solid var(--pw-line);
  color: var(--pw-mut);
  transition: background .14s, color .14s;

  &:hover {
    background: #FDF2F2;
    color: var(--pw-cr);
  }
}

.lnk {
  height: 32px;
  padding: 0 10px;
  border-radius: 9px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--pw-pur);

  &:hover { background: var(--pw-tint); }
}

/* ---------- list view ---------- */
.list-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

/* Open / Closed / Saved — the panel's grey-track segmented control (.rmsub .seg) */
.polls-tabs {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 2px;
  background: #fff;

  .seg {
    flex: 1;
    display: flex;
    gap: 2px;
    padding: 3px;
    background: var(--pw-line-2);
    border-radius: 10px;

    button {
      flex: 1;
      height: 26px;
      border-radius: 7px;
      font-size: 12px;
      font-weight: 600;
      color: var(--pw-mut);
      transition: background .14s, color .14s;

      &:hover { color: var(--pw-pur); }

      &.on {
        background: #fff;
        color: var(--pw-pur);
        box-shadow: 0 1px 2px rgba(28, 23, 32, .04);
      }
    }
  }
}

.polls-tabs-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.polls-list-content {
  min-height: 100%;
  display: flex;
  flex-direction: column;

  > div {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 10px 14px 2px;
  }
}

/* ---------- a card (.pcard) ---------- */
.poll-card {
  border: 1px solid var(--pw-line);
  border-radius: 14px;
  background: #fff;
  padding: 14px;
  margin-bottom: 10px;
  box-shadow: 0 1px 2px rgba(28, 23, 32, .03);
}

.card-header,
.draft-header-content {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
}

.closed .card-header {
  display: block;
}

.poll-question {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 14.5px;
  font-weight: 600;
  line-height: 1.4;
  color: var(--pw-ink);
  letter-spacing: -.01em;
  overflow-wrap: anywhere;
}

/* State is a grey line under the question (.pstate) */
.status-label,
.status-saved {
  display: block;
  font-size: 12px;
  color: var(--pw-mut);
}

.card-header .status-label {
  margin-top: 3px;
}

.draft-header-content { margin-bottom: 0; }

.status-saved {
  margin: 3px 0 12px;
}

.close-icon-btn,
.expand-btn {
  width: 28px;
  height: 28px;
  flex: 0 0 28px;
  border-radius: 8px;
  margin: -3px -4px 0 0;
}

.close-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--pw-line);
  color: var(--pw-mut);
  transition: background .14s, color .14s;

  &:hover {
    background: #FDF2F2;
    color: var(--pw-cr);
  }
}

.expand-btn:hover {
  background: var(--pw-paper);
  color: var(--pw-ink);
}

/* ---------- options: voting (.popt) with the result track (.pres) ---------- */
.poll-options {
  display: flex;
  flex-direction: column;
  gap: 2px;

  &.with-results { gap: 6px; }
  &.results { gap: 15px; }
}

.poll-option {
  display: flex;
  flex-direction: column;
}

.option-btn {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  min-height: 40px;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 13.5px;
  line-height: 1.4;
  color: var(--pw-ink);
  text-align: left;
  transition: background .13s;

  &:hover:not(:disabled) {
    background: var(--pw-tint);

    .check-circle { border-color: var(--pw-pur); }
  }

  &:disabled { cursor: default; }
}

.check-circle,
.circle-icon,
.radio-circle {
  width: 19px;
  height: 19px;
  flex: 0 0 19px;
  border-radius: 50%;
  border: 1.8px solid var(--pw-ring);
  background: #fff;
  display: grid;
  place-items: center;
  transition: border-color .13s;

  &.square { border-radius: 5px; }
}

.check-circle {
  &.voted { border-color: var(--pw-pur); }

  .dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: var(--pw-pur);
  }

  &.square .dot { border-radius: 2px; }
}

.opt-text {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}

.option-btn.selected .opt-text { font-weight: 600; }

.vote-count {
  flex: 0 0 auto;
  justify-self: end;
  font-size: 12px;
  font-weight: 500;
  color: var(--pw-mut);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.progress-track {
  position: relative;
  height: 6px;
  border-radius: 99px;
  background: var(--pw-line-2);
  overflow: hidden;
}

/* Under a voting row the track lines up with the label, past the radio */
.poll-option:not(.static) .progress-track {
  margin: 0 10px 4px 40px;
}

.progress-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-radius: 99px;
  background: var(--pw-pur-2);
  transition: width .35s ease-out;
}

.option-btn.selected + .progress-track .progress-bar { background: var(--pw-pur); }

/* Closed result: label, count right, slim bar beneath */
.option-row-static {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 10px;
  font-size: 13.5px;

  &:not(:last-child) { margin-bottom: 7px; }

  .opt-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--pw-ink-2);
    line-height: 1.4;
  }
}

/* ---------- saved card preview ---------- */
.draft-preview {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.preview-opt {
  display: flex;
  align-items: center;
  gap: 11px;
  min-height: 40px;
  padding: 8px 10px;
  border-radius: 10px;
  font-size: 13.5px;
  line-height: 1.4;
  color: var(--pw-ink);
}

.draft-settings {
  margin-top: 10px;
}

/* ---------- switches (XlSwitch) in a card or the form ---------- */
.results-toggle,
.setting-row {
  min-width: 0;

  :deep(.xl-switch) {
    flex-direction: row-reverse;
    justify-content: flex-end;
    gap: 10px;
  }

  :deep(.xl-switch__text b) {
    font-size: 12.5px;
    font-weight: 500;
    color: var(--pw-ink-2);
  }
}

/* ---------- the action row (.pact) ---------- */
.admin-controls,
.draft-actions,
.form-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--pw-line-2);
}

.admin-controls {
  justify-content: space-between;

  .results-toggle { flex: 1 1 auto; }
}

.push-right { margin-left: auto; }

.admin-controls :deep(.xl-btn),
.draft-actions :deep(.xl-btn),
.form-actions :deep(.xl-btn) {
  flex: 0 0 auto;
}

/* ---------- empty states ---------- */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 30px 24px;
  gap: 4px;

  p {
    margin: 0 0 4px;
    max-width: 230px;
    font-size: 12.5px;
    line-height: 1.55;
    color: var(--pw-mut);
  }

  /* A set that is empty while others are not (.rmsubempty) */
  &.compact {
    flex: 0 0 auto;
    justify-content: flex-start;
    padding: 26px 20px;

    p {
      max-width: none;
      margin: 0;
      font-size: 13px;
    }
  }
}

.empty-icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: var(--pw-line-2);
  color: var(--pw-mut);
  display: grid;
  place-items: center;
  margin-bottom: 8px;
}

/* ---------- footer: New Poll as the soft pill (.pnewwrap / .pnew) ---------- */
.polls-footer {
  flex: 0 0 auto;
}

.footer-actions {
  display: flex;
  justify-content: flex-end;
  padding: 14px;
}

.pnew {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  height: 44px;
  padding: 0 20px;
  border-radius: 99px;
  background: var(--pw-tint);
  color: var(--pw-pur);
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 2px 10px rgba(96, 46, 138, .12);
  transition: .14s;

  &:hover {
    background: var(--xl-color-primary-fixed-dim, #EADFF3);
    box-shadow: 0 4px 14px rgba(96, 46, 138, .18);
  }
}

/* ---------- loader ---------- */
.widget-loader {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 14px;

  .shimmer {
    border-radius: 14px;
    background: linear-gradient(90deg, var(--pw-line-2) 25%, var(--pw-line) 37%, var(--pw-line-2) 63%);
    background-size: 400% 100%;
    animation: polls-shimmer 1.4s ease infinite;
  }

  .tabs-shimmer {
    height: 32px;
    border-radius: 10px;
  }

  .card-shimmer { height: 128px; }
}

@keyframes polls-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

/* ---------- create / edit (.pform) ---------- */
.create-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.create-header {
  flex: 0 0 auto;
  padding: 10px 14px 0;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px 0 4px;
  margin-left: -4px;
  border-radius: 9px;
  font-size: 14.5px;
  font-weight: 600;
  color: var(--pw-ink);
  letter-spacing: -.01em;

  svg { color: var(--pw-mut); }

  &:hover {
    background: var(--pw-tint);
    color: var(--pw-pur);

    svg { color: var(--pw-pur); }
  }
}

.create-form {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 14px;
}

.error-banner {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 11px;
  background: var(--pw-cr-bg);
  color: var(--pw-cr);
  font-size: 13px;
  line-height: 1.45;
}

.poll-input {
  width: 100%;
  height: 42px;
  border: 1px solid var(--pw-line);
  border-radius: 11px;
  padding: 0 14px;
  font: inherit;
  font-size: 14.5px;
  color: var(--pw-ink);
  background: #fff;
  transition: border-color .14s, box-shadow .14s;

  &::placeholder { color: var(--pw-faint); }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: var(--pw-pur);
    box-shadow: 0 0 0 3px rgba(94, 47, 120, .1);
  }
}

.question-input { margin-bottom: 16px; }

.option-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;

  .option-input {
    flex: 1;
    min-width: 0;
  }
}

.remove-opt-btn {
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  border-radius: 8px;
  color: var(--pw-cr);
}

.add-opt-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 32px;
  padding: 0 10px 0 0;
  margin-bottom: 8px;
  color: var(--pw-pur);
  font-size: 13px;
  font-weight: 600;

  &:hover:not(:disabled) { color: var(--pw-pur-2); }

  &:disabled {
    opacity: .5;
    cursor: not-allowed;
  }
}

.poll-setting {
  padding: 10px 0;
  border-bottom: 1px solid var(--pw-line-2);

  :deep(.xl-switch) {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    gap: 14px;
  }

  :deep(.xl-switch__text b) {
    font-size: 14px;
    font-weight: 600;
    color: var(--pw-ink);
  }
}

.form-actions {
  border-top: 0;

  .right-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .polls-widget *,
  .polls-widget *::before,
  .polls-widget *::after {
    transition: none !important;
  }

  .widget-loader .shimmer { animation: none; }
}

/* ---------- Polls on a phone (prototype sheetPolls, XtendLive_Mobile_Room.html) ---------- */
.polls-phone { display: flex; flex: 1; flex-direction: column; min-height: 0; height: 100%; color: #1C1720; font-family: var(--xl-font-family); }
.pp-body { flex: 1; min-height: 0; overflow-y: auto; padding: 0 16px 12px; }
.pp-h { margin: 2px 0 12px; font-size: 15px; font-weight: 650; }

.pp-seg { display: flex; gap: 4px; margin: 2px 0 12px; padding: 3px; border-radius: 99px; background: #F3EFF5; }
.pp-seg button { display: inline-flex; flex: 1; align-items: center; justify-content: center; gap: 6px; height: 32px; border: 0; border-radius: 99px; background: none; color: #514A57; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }
.pp-seg button.on { background: #fff; color: #1C1720; box-shadow: 0 1px 3px rgba(28, 23, 32, 0.1); }
.pp-seg i { font-style: normal; color: #867D8D; font-variant-numeric: tabular-nums; }

.pp-card { margin-bottom: 9px; padding: 12px 13px; border: 1px solid #ECE6EF; border-radius: 16px; background: #fff; }
.pp-st { display: inline-flex; align-items: center; height: 22px; margin-bottom: 8px; padding: 0 8px; border-radius: 99px; font-size: 11.5px; font-weight: 600; }
.pp-st--live { background: #FBF0F7; color: #9A2D6E; }
.pp-st--no { background: #F3F1F4; color: #867D8D; }
.pp-q { margin: 0 0 10px; font-size: 14.5px; font-weight: 600; line-height: 1.4; }

/* prototype .popt */
.pp-opt { display: flex; align-items: center; gap: 10px; width: 100%; min-height: 44px; margin-bottom: 7px; padding: 10px 12px; border: 1.5px solid #E6E0EA; border-radius: 12px; background: #fff; color: #1C1720; font: inherit; font-size: 14px; text-align: left; cursor: pointer; }
.pp-opt i { display: grid; flex: 0 0 18px; place-items: center; width: 18px; height: 18px; border: 2px solid #CFC6D6; border-radius: 50%; }
.pp-opt.square i { border-radius: 5px; }
.pp-opt.on { border-color: #8154A6; background: #F5EFF9; }
.pp-opt.on i { border-color: #5E2F78; }
.pp-opt.on i::after { content: ""; width: 8px; height: 8px; border-radius: 50%; background: #5E2F78; }
.pp-opt.square.on i::after { border-radius: 2px; }

/* prototype .pres */
.pp-res { position: relative; display: flex; align-items: center; height: 40px; margin-bottom: 6px; padding: 0 12px; overflow: hidden; border-radius: 11px; background: #F2EEF4; font-size: 13.5px; }
.pp-res i { position: absolute; inset: 0 auto 0 0; border-radius: 11px; background: #E2D2EE; }
.pp-res.top i { background: linear-gradient(90deg, #8154A6, #5E2F78); }
/* The leader's words are white only where they sit on its purple, or they
   vanish into the pale track beyond it. */
.pp-res.top { color: #2A0E3C; font-weight: 600; }
.pp-res.top em { color: #5E2F78; }
.pp-res.top--label-in span { color: #fff; }
.pp-res.top--pct-in em { color: #fff; }
.pp-res span { position: relative; display: flex; flex: 1; align-items: center; gap: 6px; min-width: 0; }
.pp-res em { position: relative; font-style: normal; font-weight: 700; font-variant-numeric: tabular-nums; }
.pp-meta { margin: 6px 0 0; color: #867D8D; font-size: 12px; }
.pp-meta--c { text-align: center; }

.pp-btn { display: inline-flex; align-items: center; justify-content: center; gap: 7px; height: 42px; padding: 0 18px; border: 0; border-radius: 99px; background: linear-gradient(155deg, #8154A6, #5E2F78); color: #fff; font: inherit; font-size: 14px; font-weight: 600; cursor: pointer; }
.pp-btn:disabled { opacity: 0.45; cursor: default; }
.pp-btn--g { border: 1px solid #ECE6EF; background: #fff; color: #1C1720; }
.pp-btn--sm { height: 34px; padding: 0 14px; font-size: 13px; }
.pp-btn--blk { width: 100%; }
.pp-btn--close { margin-top: 10px; }
.pp-btn--create { margin-top: 14px; }
.pp-btn:focus-visible, .pp-opt:focus-visible, .pp-seg button:focus-visible { outline: 2px solid #5E2F78; outline-offset: 2px; }

/* prototype .fld — at least 16px, or an iPhone zooms the page when it is tapped */
.pp-fld { margin-bottom: 12px; }
.pp-fld label { display: block; margin-bottom: 5px; color: #514A57; font-size: 12px; font-weight: 600; }
.pp-fld input { box-sizing: border-box; width: 100%; height: 42px; padding: 0 13px; border: 1px solid #E2DBE6; border-radius: 12px; outline: none; background: #fff; color: #1C1720; font: inherit; font-size: 16px; }
.pp-fld input:focus { border-color: #8154A6; }
.pp-error { margin: 10px 0 0; color: #A32020; font-size: 13px; }

.pp-empty { padding: 30px 18px 26px; color: #867D8D; text-align: center; }
.pp-empty__g { display: grid; place-items: center; width: 52px; height: 52px; margin: 0 auto 12px; border-radius: 16px; background: #F5EFF9; color: #5E2F78; }
.pp-empty b { display: block; margin-bottom: 4px; color: #1C1720; font-size: 15px; }
.pp-empty p { max-width: 250px; margin: 0 auto; font-size: 13px; line-height: 1.5; }
.pp-note { padding: 22px 0; color: #867D8D; font-size: 13px; text-align: center; }
.pp-note--left { padding: 0 0 10px; text-align: left; }

.pp-foot { flex: 0 0 auto; padding: 10px 12px calc(12px + env(safe-area-inset-bottom, 0px)); border-top: 1px solid #ECE6EF; background: #fff; }
.pp-foot--two { display: flex; gap: 8px; }
.pp-foot--two .pp-btn { flex: 1; }
</style>
